import { useLocalReactionStore } from "designSystem/hooks";
import { rightPaneStore, routerStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { updateChannelOnNode, UpdateChannelOnNodeOption } from "../component/ChatStore";
import { ErrorFallback } from "../component/ErrorFallback";
import { SelectedChannel } from "../types";
import * as api from "./api";
import AddChannel from "./component/AddChannel";
import ChannelSelection from "./component/ChannelSelection";
import ConnectToSlack from "./component/ConnectToSlack";
import DisplayChannel from "./component/DisplayChannel";
import JoinSlack from "./component/JoinSlack";
import { inviteUsers, SlackStore, SlackStoreContext, useSlackStore, WidgetPanel } from "./component/SlackStore";

export interface SlackForC8ObjectProps {
	node?: C8Node;
}

export const SlackForC8Object: FunctionComponent<SlackForC8ObjectProps> = ({ node }) => {
	const store = useLocalReactionStore(() => new SlackStore());

	return (
		<SlackStoreContext.Provider value={store}>
			{store && (
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<SlackForC8ObjectInternal node={node} />
				</ErrorBoundary>
			)}
		</SlackStoreContext.Provider>
	);
};

const SlackForC8ObjectInternal: FunctionComponent<SlackForC8ObjectProps> = observer(({ node }) => {
	node = node ?? routerStore.locationNode;
	const isNodeReadOnly = !xml.nodeHasAccess(node, xml.RIGHT_WRITE);
	const { showBoundary } = useErrorBoundary();

	const store = useSlackStore();

	const {
		assignSelectedChannel,
		error,
		selectedChannel,
		setSelectedChannel,
		navigationToAddChannel,
		navigateToPanel,
		currentPanel,
		handleOnBack,
	} = store || {};

	if (error) {
		showBoundary(error);
	}

	const closeRightPane = () => {
		rightPaneStore.closeSelected();
	};

	const handleOnClose = closeRightPane;



	const handleOnConnectToSlack = () => navigateToPanel(WidgetPanel.CHANNEL_SELECTION);
	const handleOnAddNewChannel = () => navigationToAddChannel();
	const handleOnSelectChannel = async (channel: SelectedChannel) => {
		try {
			// invite our bot to the selected channel
			await inviteUsers(channel.channelId);

			assignSelectedChannel(channel);
			// C8-25599 cleanup wizard history
			navigateToPanel(WidgetPanel.DISPLAYING_CHANNEL, [WidgetPanel.CONNECT_TO_SLACK]);
		} catch (error) {
			showBoundary(error);
		}
	};

	const handleRemoveConnectionToChannel = async (channelId: string) => {
		await removeSelectedChannelForNode(node, channelId);
		// reset selected channel
		setSelectedChannel(SelectedChannel.empty());
		// reset the history and navigate to the channel list
		navigateToPanel(WidgetPanel.CONNECT_TO_SLACK, []);
	};
	const handleJoinChannel = async (channelId: string) => {
		try {
			const joinChannelJson = await api.joinChannel(channelId);
			const channel = joinChannelJson.channel;
			const { id, name, member, private: isPrivate } = channel;
			const selectedChannel = new SelectedChannel(id, name, member, isPrivate);
			setSelectedChannel(selectedChannel);
		} catch (error) {
			showBoundary(error);
		}
	};

	switch (currentPanel) {
		case WidgetPanel.JOIN_SLACK: return (
			<JoinSlack
				onBack={handleOnBack}
				onClose={handleOnClose}
			/>
		);
		case WidgetPanel.CONNECT_TO_SLACK: return (
			<ConnectToSlack
				isNodeReadOnly={isNodeReadOnly}
				nodeSlackChannels={node[ATTR_ID_SLACK_CHANNELS]}
				nodeType={node.$Type}
				onBack={handleOnBack}
				onClose={handleOnClose}
				onConnectToSlack={handleOnConnectToSlack}
				onSelectChannel={handleOnSelectChannel}
			/>
		);
		case WidgetPanel.CHANNEL_SELECTION: return (
			<ChannelSelection
				onAddNewChannel={handleOnAddNewChannel}
				onBack={handleOnBack}
				onClose={handleOnClose}
				onSelectChannel={handleOnSelectChannel}
			/>
		);
		case WidgetPanel.ADDING_CHANNEL: return (
			<AddChannel />
		);
		case WidgetPanel.DISPLAYING_CHANNEL: return (
			<DisplayChannel
				channel={selectedChannel}
				nodeType={node.$Type}
				onBack={handleOnBack}
				onClose={handleOnClose}
				onJoinChannel={handleJoinChannel}
				onRemoveConnectionToChannel={handleRemoveConnectionToChannel}
			/>
		);
	}
});

export default SlackForC8Object;

//----------------------------------------------------------------------------------------------------
//                   Utility functions
//----------------------------------------------------------------------------------------------------

const ATTR_ID_SLACK_CHANNELS = "SlackChannels";

async function removeSelectedChannelForNode(node: C8Node, channelId: string) {
	return updateChannelOnNode(node.$URL, channelId, UpdateChannelOnNodeOption.REMOVE, ATTR_ID_SLACK_CHANNELS);
}